import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ResearchSnapshotPanelView } from '@/components/investigations/ResearchSnapshotPanelView'
import { useInvestigationsStoreApi } from '@/stores/investigations-store-provider'
import type { Investigation } from '@/types/investigation'

/**
 * Connected research-snapshot panel. Owns the in-flight `busy` state and wires
 * the "Regenerate/Generate evidence" action to the investigations store
 * (re-runs the search agent), surfacing progress + result via toasts. The pure
 * {@link ResearchSnapshotPanelView} renders everything.
 */
export function ResearchSnapshotPanel({ inv }: { inv: Investigation }) {
  const { t } = useTranslation()
  const storeApi = useInvestigationsStoreApi()
  const [busy, setBusy] = useState(false)

  const handleRegenerate = async () => {
    if (busy) return
    setBusy(true)
    const pending = toast.loading(
      t('investigations.snapshot.regenerating', 'Regenerating evidence…')
    )
    try {
      await storeApi.getState().regenerateEvidence(inv.id)
      toast.success(t('investigations.snapshot.regenerated', 'Evidence regenerated'), {
        id: pending,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      toast.error(
        t('investigations.snapshot.regenerateFailed', 'Regenerate failed: {{message}}', {
          message,
        }),
        { id: pending }
      )
    } finally {
      setBusy(false)
    }
  }

  return <ResearchSnapshotPanelView inv={inv} busy={busy} onRegenerate={handleRegenerate} />
}
